import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { TimetableGrid } from "@/components/TimetableGrid";
import UpcomingEvents from "@/components/UpcomingEvents";
import { GraduationCap, Calendar, BookOpen, Bell, ChevronRight, ListChecks } from "lucide-react";

const NavCard = ({ to, icon, title, subtitle, value, tone = "oxford", testId }) => (
    <Link to={to} data-testid={testId}
        className="group bg-white rounded-2xl border border-slate-200 p-6 sm:p-7 hover:border-oxford hover:shadow-lg transition-all flex flex-col">
        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-4 ${tone === "oxford" ? "bg-oxford text-white" : tone === "sage" ? "bg-sage text-white" : tone === "terracotta" ? "bg-terracotta text-white" : "bg-seablue text-white"}`}>{icon}</div>
        <div className="text-[10px] uppercase tracking-widest text-slate-500">{subtitle}</div>
        <div className="font-heading text-xl font-bold text-slate-900 mt-1">{title}</div>
        {value && <div className="mt-3 text-2xl font-heading font-black text-oxford">{value}</div>}
        <div className="mt-auto pt-3 flex items-center gap-1.5 text-sm text-oxford font-semibold">
            Hap <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition" />
        </div>
    </Link>
);

export default function StudentDashboard() {
    const [me, setMe] = useState(null);
    const [tt, setTt] = useState({ slots: [], class_id: null });
    const [subjects, setSubjects] = useState([]);
    const [posts, setPosts] = useState([]);
    const [unread, setUnread] = useState(0);
    const [pendingAssignments, setPendingAssignments] = useState(0);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const [m, t, s, ib, asg] = await Promise.all([
                    api.get("/student/me"),
                    api.get("/student/timetable"),
                    api.get("/subjects"),
                    api.get("/notifications/inbox"),
                    api.get("/student/assignments"),
                ]);
                setMe(m.data); setTt(t.data); setSubjects(s.data); setUnread(ib.data.unread || 0);
                setPendingAssignments((asg.data || []).filter((a) => !a.opened_at && !a.is_expired).length);
                if (t.data.class_id) {
                    const r = await api.get("/lms/posts", { params: { class_id: t.data.class_id } });
                    setPosts(r.data.slice(0, 3));
                }
            } finally { setLoading(false); }
        })();
    }, []);

    if (loading) return <Layout><div className="text-slate-500">Duke ngarkuar…</div></Layout>;
    if (!me?.student) return <Layout><div className="text-slate-500">Profili nuk u gjet.</div></Layout>;

    const sid = me.student.id;
    const todayDay = (new Date().getDay() + 6) % 7; // 0=Mon..6=Sun
    const todaySlots = tt.slots.filter((s) => s.day === todayDay).sort((a, b) => a.period - b.period);
    const subjectMap = Object.fromEntries(subjects.map((s) => [s.id, s]));

    return (
        <Layout>
            <div className="mb-6">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">Mirë se erdhe</div>
                <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-black text-slate-900 tracking-tight">{me.student.name}</h1>
                <p className="text-slate-600 mt-2 text-sm">Viti {me.student.year}</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
                <NavCard to={`/student/grades`} icon={<GraduationCap className="w-6 h-6" />} subtitle="Notat" title="Notat e mia" value="" tone="oxford" testId="student-nav-grades" />
                <NavCard to={`/student/timetable`} icon={<Calendar className="w-6 h-6" />} subtitle="Orari" title="Orari javor" value={`${tt.slots.length} orë`} tone="seablue" testId="student-nav-timetable" />
                <NavCard to={`/student/lms`} icon={<BookOpen className="w-6 h-6" />} subtitle="LMS" title="Materialet" value={`${posts.length}+`} tone="sage" testId="student-nav-lms" />
                <NavCard to={`/student/assignments`} icon={<ListChecks className="w-6 h-6" />} subtitle="Detyrat" title="Detyrat e mia" value={pendingAssignments > 0 ? `${pendingAssignments} pritur` : "0"} tone="terracotta" testId="student-nav-assignments" />
            </div>

            <div className="mb-6"><UpcomingEvents days={14} limit={4} /></div>

            {todaySlots.length > 0 && (
                <div className="mb-6 bg-white border border-slate-200 rounded-2xl p-5">
                    <div className="flex items-center justify-between mb-3">
                        <div>
                            <div className="text-[10px] uppercase tracking-widest text-slate-500">Sot</div>
                            <h2 className="font-heading text-xl font-bold text-slate-900">Lëndët e ditës</h2>
                        </div>
                        <Bell className="w-5 h-5 text-oxford" />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {todaySlots.slice(0, 8).map((s) => (
                            <div key={`${s.day}-${s.period}`} className="bg-bone/40 border border-slate-200 rounded-md px-3 py-2 text-xs">
                                <div className="text-[10px] text-slate-500 font-mono">Ora {s.period}</div>
                                <div className="font-bold text-slate-900 truncate">{(subjectMap[s.subject_id] || {}).name || "—"}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {posts.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="student-recent-lms">
                    <h2 className="font-heading text-lg font-bold text-slate-900 mb-3">Materialet e fundit</h2>
                    <div className="divide-y divide-slate-200">
                        {posts.map((p) => (
                            <div key={p.id} className="py-3 flex items-start gap-3">
                                <div className="w-9 h-9 rounded-lg bg-sage/10 text-sage flex items-center justify-center shrink-0"><BookOpen className="w-5 h-5" /></div>
                                <div className="min-w-0 flex-1">
                                    <div className="text-xs text-slate-500">{p.subject_name} • {p.created_at?.slice(0, 10)}</div>
                                    <div className="font-bold text-slate-900 truncate">{p.title}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </Layout>
    );
}
